'use client'

import React from 'react'

import {
  Container,
  MoreButton,
  MoreButtonContainer,
  TextContainer,
} from './styles'

export interface ReadMoreProps {
  children: React.ReactNode
  truncateLines?: number
  lineHeight?: string
  fixedButton?: boolean
}

export const ReadMore = ({
  children,
  truncateLines = 4,
  lineHeight = '26px',
  fixedButton = false,
}: ReadMoreProps) => {
  const [isExpanded, setIsExpanded] = React.useState(false)
  const [height, setHeight] = React.useState<string>('')
  const [shouldDisplayButton, setShouldDisplayButton] = React.useState(false)

  const textContainerRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    const lineHeightValue = parseFloat(lineHeight)
    const unit = lineHeight.includes('px') ? 'px' : 'em'

    if (!textContainerRef.current) return

    const maxHeight = `${truncateLines * lineHeightValue}${unit}`
    const isOverflowing =
      textContainerRef.current.scrollHeight > parseFloat(maxHeight)
    const fullHeight = `${textContainerRef.current.scrollHeight}px`

    if (isExpanded) {
      setHeight(fullHeight)
    } else {
      setHeight(
        isOverflowing
          ? `${truncateLines * lineHeightValue}${unit}`
          : fullHeight,
      )
      setShouldDisplayButton(isOverflowing)
    }
  }, [isExpanded, lineHeight, truncateLines])

  React.useEffect(() => {
    const handleResize = () => {
      if (textContainerRef.current) {
        const lineHeightValue = parseFloat(lineHeight)
        const unit = lineHeight.includes('px') ? 'px' : 'em'
        const maxHeight = `${truncateLines * lineHeightValue}${unit}`
        const isOverflowing =
          textContainerRef.current.scrollHeight > parseFloat(maxHeight)
        setShouldDisplayButton(isExpanded || isOverflowing)
      }
    }

    if (textContainerRef.current) {
      const resizeObserver = new ResizeObserver(handleResize)
      resizeObserver.observe(textContainerRef.current)

      return () => {
        resizeObserver.disconnect()
      }
    }
  }, [height, lineHeight, isExpanded, truncateLines])

  const toggleExpanded = () => {
    setIsExpanded((prev) => !prev)
  }

  return (
    <Container>
      <TextContainer
        ref={textContainerRef}
        isExpanded={isExpanded}
        height={height}
        truncateLines={truncateLines}
      >
        {children}
      </TextContainer>

      <MoreButtonContainer
        show={shouldDisplayButton}
        fixedPosition={fixedButton}
      >
        <MoreButton onClick={toggleExpanded}>
          {isExpanded ? '閉じる' : '続きを読む'}
        </MoreButton>
      </MoreButtonContainer>
    </Container>
  )
}
